import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  useDistrictFetchData,
  useFetchCountriesAllData,
  useFetchCountryCitiesAllData,
  useFetchCountryWarehouseAllData,
} from "../../../apis/locationApis";
import {
  useCreateOutletMutation,
  useUpdateOutletMutation,
} from "../../../apis/outletApis";
import SelectInput from "../../../components/Select";
import { useLanguage } from "../../../context/LanguageContext";
import InputField from "../../../components/common/InputField";
import { toast } from "react-hot-toast";
import { IconX, IconUser, IconMapPin } from "@tabler/icons-react";
import OutletGoogleMap from "./OutletGoogleMap";
import {
  GULF_COUNTRIES,
  getCountryAddressConfig,
} from "../../OrderManagement/_components/GulfMapPicker";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";

const API_BASE = "https://track-map.gwarm.net/api/areas";

interface AddNewOutletProps {
  onSuccess?: () => void;
  onClose: () => void;
  isSidebar?: boolean;
  outletId?: string;
  outletData?: any;
}

// ── Tiny spinner ──
const Spinner = () => (
  <svg
    className="animate-spin h-4 w-4 text-blue-500"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
);

const SectionHeader = ({
  icon: Icon,
  title,
  className = "border-b border-[#FBD037]/30 pb-2 mb-3",
}: {
  icon: any;
  title: string;
  className?: string;
}) => {
  const { t } = useLanguage();
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {t(title)}
      </p>
    </div>
  );
};

const AddNewOutlet = ({
  onSuccess,
  onClose,
  isSidebar = false,
  outletId,
  outletData,
}: AddNewOutletProps) => {
  const { t, language } = useLanguage();
  const [selectedLocation, setSelectedLocation] = useState<any>({});
  const [shortAddressLoading, setShortAddressLoading] = useState(false);
  const [shortAddressError, setShortAddressError] = useState("");
  const [shortAddressWarning, setShortAddressWarning] = useState("");
  const [showMap, setShowMap] = useState(false);

  // Pin state for map sync
  const [pinPosition, setPinPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [snappedPosition, setSnappedPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const isPinnedRef = useRef(false);

  const [pendingAddressInfo, setPendingAddressInfo] = useState<{
    countryName?: string;
    cityName?: string;
    districtName?: string;
  } | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      outletName: "",
      email: "",
      mobileNo: "",
      shortAddress: "",
      latitude: "",
      longitude: "",
      address: "",
      country: "",
      city: "",
      district: "",
      warehouseId: "",
    },
  });

  const { mutate: createOutlet, isPending: isCreatePending } =
    useCreateOutletMutation();
  const { mutate: updateOutlet, isPending: isUpdatePending } =
    useUpdateOutletMutation(outletId || "");

  const isEditMode = !!outletId;
  const isPending = isEditMode ? isUpdatePending : isCreatePending;

  const { data: countries = [] } = useFetchCountriesAllData();
  const { data: warehouseData, isFetching: isWarehousesFetching } =
    useFetchCountryWarehouseAllData();
  const warehouses = useMemo(
    () =>
      Array.isArray(warehouseData)
        ? warehouseData
        : warehouseData?.warehouses || [],
    [warehouseData],
  );
  const selectedWarehouseId = watch("warehouseId");
  const selectedWarehouse = useMemo(
    () =>
      warehouses.find((warehouse: any) => warehouse.id === selectedWarehouseId),
    [warehouses, selectedWarehouseId],
  );

  const {
    data: cities = [],
    refetch: fetchCities,
    isFetching: isCitiesFetching,
  } = useFetchCountryCitiesAllData(selectedLocation?.country?.id, "", "map");

  const {
    data: districts = [],
    refetch: fetchDistricts,
    isFetching: isDistrictsFetching,
  } = useDistrictFetchData(selectedLocation?.city?.id, "", "map");

  useEffect(() => {
    if (outletData && warehouses.length > 0) {
      let whId = outletData.warehouseId || outletData.warehouse?.id || "";
      let warehouse = warehouses.find((item: any) => item.id === whId);

      if (!warehouse) {
        // Fallback: match warehouse by country, city, and district
        warehouse = warehouses.find(
          (item: any) =>
            String(item.country) === String(outletData.country) &&
            String(item.city) === String(outletData.city) &&
            String(item.district) === String(outletData.district),
        );
        if (warehouse) {
          whId = warehouse.id;
        }
      }

      reset({
        outletName: outletData.outletName || "",
        email: outletData.email || "",
        mobileNo: outletData.mobileNo || "",
        shortAddress: outletData.shortAddress || "",
        latitude: outletData.latitude || "",
        longitude: outletData.longitude || "",
        address: outletData.address || "",
        warehouseId: whId,
      });

      if (warehouse) {
        setSelectedLocation({
          country: { id: warehouse.country, name: warehouse.countryName },
          city: { id: warehouse.city, name: warehouse.cityName },
          district: { id: warehouse.district, name: warehouse.districtName },
        });
        setValue("country", warehouse.country);
        setValue("city", warehouse.city);
        setValue("district", warehouse.district);
      } else {
        const countryObj = countries.find(
          (c: any) =>
            c.name?.toLowerCase() ===
            (outletData.countryName || outletData.country)?.toLowerCase(),
        );
        setSelectedLocation({
          country: countryObj || {
            id: outletData.country,
            name: outletData.countryName,
          },
          city: { id: outletData.city, name: outletData.cityName },
          district: { id: outletData.district, name: outletData.districtName },
        });
        setValue("country", countryObj?.id || outletData.country);
        setValue("city", outletData.city);
        setValue("district", outletData.district);
      }

      if (outletData.latitude && outletData.longitude) {
        const pos = {
          lat: parseFloat(outletData.latitude),
          lng: parseFloat(outletData.longitude),
        };
        setPinPosition(pos);
        setSnappedPosition(pos);
      }
    }
  }, [outletData, warehouses, countries, reset, setValue]);

  // ── Detect country code & config ──
  const selectedCountryId = watch("country");
  const selectedCountryObj = useMemo(
    () => countries.find((c: any) => c.id === selectedCountryId),
    [countries, selectedCountryId],
  );

  const countryCode = useMemo(() => {
    if (!selectedCountryObj) return "SA";
    return (
      GULF_COUNTRIES.find(
        (c: any) =>
          c.name.toLowerCase() === selectedCountryObj.name?.toLowerCase(),
      )?.code || "OTHER"
    );
  }, [selectedCountryObj]);

  const addressConfig = useMemo(
    () => getCountryAddressConfig(countryCode),
    [countryCode],
  );

  const isSaudiArabia = useMemo(() => countryCode === "SA", [countryCode]);

  useEffect(() => {
    if (selectedLocation?.country?.id) {
      fetchCities();
    }
  }, [selectedLocation?.country?.id, fetchCities]);

  useEffect(() => {
    if (selectedLocation?.city?.id) {
      fetchDistricts();
    }
  }, [selectedLocation?.city?.id, fetchDistricts]);

  // Reactive sequential resolver for map/short-address selections
  useEffect(() => {
    if (!pendingAddressInfo) return;
    // Warehouse selection is authoritative; address lookup must never replace it.
    if (selectedWarehouse) {
      setPendingAddressInfo(null);
      return;
    }

    if (pendingAddressInfo.countryName && countries.length > 0) {
      const countryObj = countries.find(
        (c: any) =>
          c.name.toLowerCase() ===
          pendingAddressInfo.countryName?.toLowerCase(),
      );
      if (countryObj) {
        // Only update selectedLocation if country actually changed — avoids wiping city/district
        const alreadySelected = selectedLocation?.country?.id === countryObj.id;
        if (!alreadySelected) {
          setSelectedLocation({
            country: countryObj,
            city: undefined,
            district: undefined,
          });
          setValue("country", countryObj.id, { shouldValidate: true });
        }
        setPendingAddressInfo((prev: any) => ({
          ...prev,
          countryName: undefined,
        }));
        return;
      }
    }

    if (pendingAddressInfo.cityName && cities.length > 0 && !isCitiesFetching) {
      const cityObj = cities.find(
        (c: any) =>
          c.name.toLowerCase() === pendingAddressInfo.cityName?.toLowerCase(),
      );
      if (cityObj) {
        setSelectedLocation((prev: any) => ({
          ...prev,
          city: cityObj,
          district: undefined,
        }));
        setValue("city", cityObj.id, { shouldValidate: true });
        setPendingAddressInfo((prev: any) => ({
          ...prev,
          cityName: undefined,
        }));
        return;
      }
    }

    if (
      pendingAddressInfo.districtName &&
      districts.length > 0 &&
      !isDistrictsFetching
    ) {
      const districtObj = districts.find(
        (d: any) =>
          d.name.toLowerCase() ===
          pendingAddressInfo.districtName?.toLowerCase(),
      );
      if (districtObj) {
        setSelectedLocation((prev: any) => ({
          ...prev,
          district: districtObj,
        }));
        setValue("district", districtObj.id, { shouldValidate: true });
      }
      setPendingAddressInfo(null);
    }
  }, [
    countries,
    cities,
    districts,
    pendingAddressInfo,
    setValue,
    selectedLocation?.country?.id,
    isCitiesFetching,
    isDistrictsFetching,
    selectedWarehouse,
  ]);

  const handleWarehouseChange = (value: string) => {
    const warehouse = warehouses.find((item: any) => item.id === value);

    setValue("warehouseId", value, { shouldValidate: true });
    clearErrors(["warehouseId", "country", "city", "district", "shortAddress"]);

    if (!warehouse) {
      setSelectedLocation({});
      setValue("country", "");
      setValue("city", "");
      setValue("district", "");
      return;
    }

    setSelectedLocation({
      country: { id: warehouse.country, name: warehouse.countryName },
      city: { id: warehouse.city, name: warehouse.cityName },
      district: { id: warehouse.district, name: warehouse.districtName },
    });

    setValue("country", warehouse.country, { shouldValidate: true });
    setValue("city", warehouse.city, { shouldValidate: true });
    setValue("district", warehouse.district, { shouldValidate: true });
    setValue("shortAddress", "");
    setValue("address", "");
    setValue("latitude", "");
    setValue("longitude", "");
    setPinPosition(null);
    setSnappedPosition(null);
    setShortAddressError("");
    setShortAddressWarning("");
  };

  const validateWarehouseCity = useCallback(
    (addressCity?: string) => {
      if (!selectedWarehouse || !addressCity) return true;
      const normalize = (value: string) => value.trim().toLocaleLowerCase();
      if (
        normalize(addressCity) === normalize(selectedWarehouse.cityName || "")
      )
        return true;

      const message = `This short address belongs to ${addressCity}, but the selected warehouse is in ${selectedWarehouse.cityName}.`;
      setShortAddressError(message);
      setError("shortAddress", { type: "validate", message });
      return false;
    },
    [selectedWarehouse, setError],
  );

  const handleCityChange = (value: string) => {
    setSelectedLocation((prev: any) => ({
      ...prev,
      city: { id: value },
      district: undefined,
    }));

    setValue("city", value, { shouldValidate: true });
  };

  const handleDistrictChange = (value: string) => {
    setSelectedLocation((prev: any) => ({
      ...prev,
      district: { id: value },
    }));

    setValue("district", value, { shouldValidate: true });
  };

  // ── Map pick handler — called when user clicks/drags on map ──
  const handleMapPick = useCallback(
    async (locData: any) => {
      const {
        lat,
        lng,
        address,
        shortAddress,
        cityName,
        districtName,
        source,
      } = locData;

      // Update pin position
      setPinPosition({ lat, lng });

      // If DB returned precise coordinates, snap
      if (source === "redis_geo" || source === "database_zone") {
        setSnappedPosition({ lat, lng });
      } else {
        setSnappedPosition(null);
      }

      // Always fill lat/lng and address from map
      setValue("latitude", lat.toString());
      setValue("longitude", lng.toString());
      setValue("address", address || "");

      // Short address handling — only relevant for Saudi Arabia
      if (isSaudiArabia) {
        if (shortAddress) {
          setValue("shortAddress", shortAddress);
          setShortAddressError("");
          setShortAddressWarning("");
          setShortAddressLoading(true);

          try {
            const res = await fetch(
              `${API_BASE}/adrress-get-by-short-address?shortAddress=${shortAddress}`,
            );
            const checkData = await res.json();
            if (checkData?.success && checkData?.data) {
              const shortAddr = checkData.data?.dataValues || checkData.data;
              if (!validateWarehouseCity(shortAddr.city)) return;
              setValue(
                "address",
                `${shortAddr.buildingNumber || ""} ${shortAddr.street || ""} ${shortAddr.district || ""} ${shortAddr.city || ""}`.trim(),
              );

              if (shortAddr.latitude && shortAddr.longitude) {
                const pos = {
                  lat: parseFloat(shortAddr.latitude),
                  lng: parseFloat(shortAddr.longitude),
                };
                setPinPosition(pos);
                setSnappedPosition(pos);
                setValue("latitude", shortAddr.latitude.toString());
                setValue("longitude", shortAddr.longitude.toString());
              }

              setPendingAddressInfo({
                countryName: "Saudi Arabia",
                cityName: shortAddr.city,
                districtName: shortAddr.district,
              });

              if (source === "database_zone") {
                setShortAddressWarning(
                  "📍 Approximate match. Pin again on the exact spot for precise short address.",
                );
              } else {
                setShortAddressWarning("");
              }
              setShortAddressError("");
            } else {
              setShortAddressError(
                "Short Address verified lookup failed. Please type it manually.",
              );
            }
          } catch (err) {
            console.error("Short address verification lookup failed:", err);
            setShortAddressError(
              "Could not verify short address. Please try again.",
            );
          } finally {
            setShortAddressLoading(false);
          }
        } else {
          // No short address from map — clear short address field but keep the geocoded full address
          setValue("shortAddress", "");
          setShortAddressError("");
          setShortAddressWarning(
            "Short Address not found from map. Please type it manually below.",
          );
          // Still resolve city/district from geocoding
          setPendingAddressInfo({
            countryName: "Saudi Arabia",
            cityName: cityName,
            districtName: districtName,
          });
        }
      } else {
        // Non-SA: resolve country/city/district from map data
        setPendingAddressInfo({
          countryName: locData.countryName || "",
          cityName: cityName,
          districtName: districtName,
        });
      }

      // For SA with shortAddress found, city/district resolved in the API block above
      isPinnedRef.current = true;
    },
    [setValue, isSaudiArabia, validateWarehouseCity],
  );

  // ── Short address manual input handler ──
  const handleShortAddressChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.toUpperCase();
      setValue("shortAddress", val);
      setShortAddressWarning("");
      setShortAddressError("");

      if (val.length === 8) {
        setShortAddressLoading(true);
        try {
          const res = await fetch(
            `${API_BASE}/adrress-get-by-short-address?shortAddress=${val}`,
          );
          const apiData = await res.json();
          if (apiData?.success && apiData?.data) {
            const addr = apiData.data?.dataValues || apiData.data;
            if (!validateWarehouseCity(addr.city)) return;
            setShortAddressError("");
            clearErrors("shortAddress");

            // Fill address
            setValue(
              "address",
              `${addr.buildingNumber || ""} ${addr.street || ""} ${addr.district || ""} ${addr.city || ""}`.trim(),
            );

            // Move map pin to the short address coordinates
            if (addr.latitude && addr.longitude) {
              const lat = parseFloat(addr.latitude);
              const lng = parseFloat(addr.longitude);
              const pos = { lat, lng };
              setPinPosition(pos);
              setSnappedPosition(pos);
              setValue("latitude", addr.latitude.toString());
              setValue("longitude", addr.longitude.toString());
            }

            // Trigger country/city/district resolution
            setPendingAddressInfo({
              countryName: "Saudi Arabia",
              cityName: addr.city,
              districtName: addr.district,
            });
          } else {
            setShortAddressError(
              "Short code not found. Please check and enter a correct code.",
            );
          }
        } catch (err) {
          console.error("Short address lookup failed:", err);
          setShortAddressError(
            "Could not verify short address. Please try again.",
          );
        } finally {
          setShortAddressLoading(false);
        }
      } else if (val.length === 0) {
        setPinPosition(null);
        setSnappedPosition(null);
      }
    },
    [setValue, validateWarehouseCity, clearErrors],
  );

  // ── Toggle map ──
  const handleToggleMap = useCallback(() => {
    setShowMap((prev) => {
      if (!prev) isPinnedRef.current = false;
      return !prev;
    });
  }, []);

  const onSubmit = (data: any) => {
    if (shortAddressLoading || shortAddressError) {
      setError("shortAddress", {
        type: "validate",
        message:
          shortAddressError ||
          "Please wait while the short address is verified",
      });
      return;
    }

    const payload = {
      outletName: data.outletName,
      email: data.email,
      mobileNo: data.mobileNo,
      shortAddress: data.shortAddress || "",
      latitude: data.latitude || "",
      longitude: data.longitude || "",
      address: data.address,
      country: data.country,
      city: data.city,
      district: data.district,
      warehouseId: data.warehouseId,
    };

    const submitFn = isEditMode ? updateOutlet : createOutlet;
    const successMsg = isEditMode
      ? t("Outlet updated successfully")
      : t("Outlet created successfully");

    submitFn(payload, {
      onSuccess: () => {
        toast.success(successMsg);
        reset();
        setSelectedLocation({});
        setPinPosition(null);
        setSnappedPosition(null);
        onSuccess?.();
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          (isEditMode ? "Failed to update outlet" : "Failed to create outlet");

        toast.error(t(message));
      },
    });
  };

  const isPinned = !!pinPosition;
  const shortAddressVal = watch("shortAddress");
  const shortAddressOk =
    !shortAddressLoading &&
    !shortAddressError &&
    shortAddressVal?.length === 8 &&
    watch("address");

  if (isSidebar) {
    return (
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="relative bg-white/20 rounded-3xl shadow-sm border flex flex-col overflow-hidden h-full"
      >
        <input
          type="hidden"
          {...register("country", { required: t("country is required") })}
        />
        <input
          type="hidden"
          {...register("city", { required: t("city is required") })}
        />
        <input
          type="hidden"
          {...register("district", { required: t("district is required") })}
        />
        <input
          type="hidden"
          {...register("warehouseId", { required: "Warehouse is required" })}
        />

        {/* Header */}
        <div className="flex items-center flex-shrink-0 justify-between px-6 py-4 border-b border-gray-200 bg-white/20">
          <h1 className="text-xl font-semibold text-[#212121]">
            {isEditMode ? t("Edit Outlet") : t("Add New Outlet")}
          </h1>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-1.5 bg-[#FBD037] text-[#212121] rounded-lg text-xs disabled:opacity-50"
            >
              {isEditMode
                ? isPending
                  ? t("Updating...")
                  : t("Update")
                : isPending
                  ? t("Adding...")
                  : t("Add")}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 bg-white"
              title="Close"
            >
              <IconX size={16} />
            </button>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="w-full flex-1 overflow-y-auto bg-white/20 min-h-0">
          <div className="space-y-2 p-4 scrollbar-clean">
            {/* ═══ Basic Info Section ═══ */}
            <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 relative">
              <div className="flex justify-between items-center  border-b border-[#FBD037]/30 pb-2 mb-3">
                <SectionHeader
                  className=""
                  icon={IconUser}
                  title={t("Details")}
                />
                <div className="space-y-1">
                  <SelectInput
                    className="flex items-center gap-2"
                    name="warehouseId"
                    label={t("Warehouse")}
                    placeholder={
                      isWarehousesFetching
                        ? t("Loading warehouses...")
                        : t("Select warehouse")
                    }
                    selectOptions={warehouses.map((warehouse: any) => ({
                      id: warehouse.id,
                      value: warehouse.id,
                      text: warehouse.name,
                    }))}
                    validation={{ required: t("Warehouse is required") }}
                    value={watch("warehouseId")}
                    onChange={handleWarehouseChange}
                    disabled={isWarehousesFetching}
                  />
                  {errors.warehouseId?.message && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.warehouseId.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
              <div className="grid grid-cols-2 gap-2 relative">
                {/* Outlet Name */}
                <div className="space-y-1">
                  <label className="text-[#1E293B] text-xs">
                    {t("Name")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("outletName", {
                      required: t("Outlet name is required"),
                      pattern: {
                        value: /^[a-zA-Z0-9\s\-]+$/,
                        message: t(
                          "outlet name must contain only alphanumeric characters, spaces, and hyphens",
                        ),
                      },
                      validate: (value: string) => {
                        if (!value?.trim())
                          return t(
                            "outlet name cannot be empty or only spaces",
                          );
                        return true;
                      },
                    })}
                    placeholder="e.g., Delhi Central"
                    className={`w-full rounded-xl h-10 px-4 border outline-none text-xs bg-white ${
                      errors.outletName ? "border-red-500" : "border-gray-200"
                    } focus:border-[#FBD037]`}
                  />
                  {errors.outletName?.message && (
                    <p className="text-xs text-red-500">
                      {errors.outletName.message}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-[#1E293B] text-xs">
                    {t("Outlet Mobile")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    maxLength={10}
                    inputMode="numeric"
                    {...register("mobileNo", {
                      required: t("mobile is required"),
                      pattern: {
                        value: /^\d{9,10}$/,
                        message: t("mobile number must be 9 or 10 digits"),
                      },
                      validate: (value: string) => {
                        const trimmed = value?.trim();
                        if (/^0+$/.test(trimmed))
                          return t("mobile number cannot be all zeros");
                        return true;
                      },
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);
                        setValue("mobileNo", value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      },
                    })}
                    placeholder="96687651235"
                    className={`w-full rounded-xl h-10 px-4 border outline-none text-xs bg-white ${
                      errors.mobileNo ? "border-red-500" : "border-gray-200"
                    } focus:border-[#FBD037]`}
                  />
                  {errors.mobileNo?.message && (
                    <p className="text-xs text-red-500">
                      {errors.mobileNo.message}
                    </p>
                  )}
                </div>
                <div className="col-span-2 space-y-1">
                  <label className="text-[#1E293B] text-xs">
                    {t("Email")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    {...register("email", {
                      required: t("Email is required"),
                      pattern: {
                        value: /^\S+@\S+$/,
                        message: t("invalid email address"),
                      },
                    })}
                    placeholder="outlet@email.com"
                    className={`w-full rounded-xl h-10 px-4 border outline-none text-xs bg-white ${
                      errors.email ? "border-red-500" : "border-gray-200"
                    } focus:border-[#FBD037]`}
                  />
                  {errors.email?.message && (
                    <p className="text-xs text-red-500">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 relative">
              <SectionHeader icon={IconUser} title={t("Primary Details")} />

              <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
              <div className="grid grid-cols-3 gap-2 relative">
                <NewinputField title={"Contact Person Name"} name={""} />
                <NewinputField title={"Mobile No"} name={""} />
                <NewinputField title={"Email"} name={""} />
              </div>
            </div>

            {/* ═══ Address Section ═══ */}
            <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 relative">
              <div className="flex justify-between items-center w-full border-b border-[#FBD037]/30 pb-2 mb-3">
                <SectionHeader
                  className=""
                  icon={IconMapPin}
                  title={t("Address")}
                />
                <button
                  type="button"
                  onClick={handleToggleMap}
                  className="px-3 py-1.5 rounded-md bg-[#4F6EF7] text-white text-xs font-semibold hover:bg-[#3d59c9] transition-colors"
                >
                  {showMap ? t("Hide Map") : t("Open Map")}
                </button>
              </div>
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
              <div className="space-y-4 relative">
                {showMap && (
                  <OutletGoogleMap
                    onLocationPicked={handleMapPick}
                    pinPosition={pinPosition}
                    snappedPosition={snappedPosition}
                  />
                )}
                <div className="grid grid-cols-6 gap-4 relative">
                  {isSaudiArabia && (
                    <div className="col-span-3 relative space-y-1">
                      <label className="text-[#1E293B] text-xs">
                        {t("Short Address")}{" "}
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative">
                        <input
                          {...(() => {
                            const { onChange: formOnChange, ...rest } =
                              register("shortAddress", {
                                required: t("short address is required"),
                                pattern: {
                                  value: /^[A-Z0-9]{8}$/,
                                  message: t(
                                    "enter a valid 8-character short address",
                                  ),
                                },
                              });
                            return {
                              ...rest,
                              onChange: (e: any) => {
                                formOnChange(e);
                                handleShortAddressChange(e);
                              },
                            };
                          })()}
                          placeholder="RHOO2375"
                          autoComplete="off"
                          disabled={shortAddressLoading}
                          className={`w-full rounded-xl h-10 px-4 text-xs border outline-none pr-9 transition-all
                            ${
                              shortAddressLoading
                                ? "border-blue-300 bg-blue-50"
                                : shortAddressError
                                  ? "border-red-400 bg-red-50"
                                  : shortAddressWarning
                                    ? "border-yellow-500 bg-yellow-50"
                                    : "border-gray-200 bg-white"
                            } focus:border-[#FBD037]`}
                        />
                        <div className="absolute inset-y-0 right-2 flex items-center">
                          {shortAddressLoading && <Spinner />}
                          {!shortAddressLoading && shortAddressError && (
                            <span className="text-red-500 text-base">✕</span>
                          )}
                          {shortAddressOk && (
                            <span className="text-green-500 text-base">✓</span>
                          )}
                        </div>
                      </div>
                      {shortAddressLoading && (
                        <div className="flex items-center gap-1.5">
                          <div
                            className="h-2 w-2 rounded-full bg-blue-400 animate-bounce"
                            style={{ animationDelay: "0ms" }}
                          />
                          <div
                            className="h-2 w-2 rounded-full bg-blue-400 animate-bounce"
                            style={{ animationDelay: "150ms" }}
                          />
                          <div
                            className="h-2 w-2 rounded-full bg-blue-400 animate-bounce"
                            style={{ animationDelay: "300ms" }}
                          />
                          <span className="text-xs text-blue-500 font-medium">
                            {t("looking up address…")}
                          </span>
                        </div>
                      )}
                      {shortAddressError && !shortAddressLoading && (
                        <p className=" text-xs text-red-500">
                          ⚠ {shortAddressError}
                        </p>
                      )}
                      {errors.shortAddress?.message &&
                        !shortAddressError &&
                        !shortAddressLoading && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.shortAddress.message}
                          </p>
                        )}
                      {shortAddressWarning &&
                        !shortAddressError &&
                        !shortAddressLoading && (
                          <span className="text-xs text-yellow-600  block font-medium">
                            {shortAddressWarning}
                          </span>
                        )}
                    </div>
                  )}

                  {/* City */}
                  <div className={isSaudiArabia ? "col-span-3 " : "col-span-6"}>
                    <NewSelectInput
                      name="city"
                      label={addressConfig.cityLabel}
                      placeholder={addressConfig.cityPlaceholder}
                      selectOptions={cities.map((city: any) => ({
                        id: city.id,
                        value: city.id,
                        text: city.name,
                      }))}
                      validation={{
                        required: `${addressConfig.cityLabel} is required`,
                      }}
                      value={selectedLocation?.city?.id}
                      onChange={handleCityChange}
                      disabled
                    />
                  </div>

                  {/* District */}
                  <div className="col-span-2">
                    <NewSelectInput
                      name="district"
                      label={
                        addressConfig.districtLabel ===
                        "District / Neighbourhood"
                          ? "District"
                          : addressConfig.districtLabel
                      }
                      labelClass=""
                      placeholder={addressConfig.districtPlaceholder}
                      selectOptions={districts.map((district: any) => ({
                        id: district.id,
                        value: district.id,
                        text: district.name,
                      }))}
                      validation={{
                        required: `${addressConfig.districtLabel} is required`,
                      }}
                      value={selectedLocation?.district?.id}
                      onChange={handleDistrictChange}
                      disabled
                    />
                  </div>

                  {/* Latitude */}
                  <div className="col-span-2 space-y-1">
                    <label className="text-[#1E293B] text-xs">
                      {t("Latitude")}
                    </label>
                    <input
                      {...register("latitude")}
                      placeholder="e.g. 24.7136"
                      className={`w-full rounded-xl h-10 px-4 border outline-none text-xs bg-white ${
                        errors.latitude ? "border-red-500" : "border-gray-200"
                      } focus:border-[#FBD037]`}
                    />
                    {errors.latitude?.message && (
                      <p className="  text-xs text-red-500">
                        {errors.latitude.message}
                      </p>
                    )}
                  </div>

                  {/* Longitude */}
                  <div className="col-span-2 space-y-1">
                    <label className="text-[#1E293B] text-xs">
                      {t("Longitude")}
                    </label>
                    <input
                      {...register("longitude")}
                      placeholder="e.g. 46.6753"
                      className={`w-full rounded-xl h-10 px-4 border outline-none text-xs bg-white ${
                        errors.longitude ? "border-red-500" : "border-gray-200"
                      } focus:border-[#FBD037]`}
                    />
                    {errors.longitude?.message && (
                      <p className="  text-xs text-red-500">
                        {errors.longitude.message}
                      </p>
                    )}
                  </div>

                  {/* Full Address Line */}
                  <div className="col-span-6 space-y-1">
                    <label className="text-[#1E293B] text-xs">
                      {t("Full Address Line")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register("address", {
                        required: t("full address is required"),
                      })}
                      placeholder={t("Enter full address details")}
                      className={`w-full rounded-xl h-10 px-4 border outline-none text-xs bg-white ${
                        errors.address ? "border-red-500" : "border-gray-200"
                      } focus:border-[#FBD037]`}
                    />
                    {errors.address?.message && (
                      <p className="text-xs text-red-500">
                        {errors.address.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <input
        type="hidden"
        {...register("country", { required: "Country is required" })}
      />
      <input
        type="hidden"
        {...register("city", { required: "City is required" })}
      />
      <input
        type="hidden"
        {...register("district", { required: "District is required" })}
      />
      <input
        type="hidden"
        {...register("warehouseId", { required: "Warehouse is required" })}
      />

      <div
        dir={language === "ar" ? "rtl" : "ltr"}
        className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-end p-4"
      >
        <div className="rounded-2xl  xl:w-7/12 lg:w-8/12 w-11/12 relative">
          <div className="flex items-center">
            <div className=" bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex items-center justify-between px-6 py-4   w-full rounded-tl-3xl rounded-tr-[63px] ">
              <h1 className="text-2xl">
                {" "}
                {isEditMode ? t("Edit Outlet") : t("Add New Outlet")}
              </h1>
            </div>
            <div className="relative ">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={38}
                height={37}
                viewBox="0 0 38 37"
                fill="none"
                className="absolute -bottom-[10px] -left-[8px] rotate-[-1.8deg]"
              >
                <path
                  d="M0.5 0L4.86189 10.5496C10.4737 24.1222 22.9306 33.645 37.5 35.5L4.91509 37L0.5 0Z"
                  fill="#F7F6F2"
                />
              </svg>

              <button
                type="button"
                onClick={onClose}
                className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 ml-5 flex items-center justify-center flex-shrink-0"
              >
                <IconX size={20} />
              </button>
            </div>
          </div>
          <div className="w-full space-y-4 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-tr-[30px]">
            <div className="h-[calc(100vh-10rem)] overflow-y-auto space-y-4 pt-0 p-4 px-6 scrollbar-clean">
              {/* ═══ Basic Info Section ═══ */}
              <div className="bg-white p-4 mt-2 rounded-xl shadow-md">
                <div className="grid grid-cols-6 gap-4">
                  <InputField
                    className="col-span-6"
                    label={t("Outlet Name")}
                    placeholder="e.g., Delhi Central"
                    {...register("outletName", {
                      required: "Outlet name is required",
                      pattern: {
                        value: /^[a-zA-Z0-9\s\-]+$/,
                        message:
                          "Outlet name must contain only alphanumeric characters, spaces, and hyphens",
                      },
                      validate: (value: string) => {
                        if (!value?.trim())
                          return "Outlet name cannot be empty or only spaces";
                        return true;
                      },
                    })}
                    error={errors.outletName?.message}
                  />

                  {/* Warehouse controls the outlet's permitted location */}
                  <div className="col-span-6">
                    <SelectInput
                      name="warehouseId"
                      label="Warehouse"
                      placeholder={
                        isWarehousesFetching
                          ? "Loading warehouses..."
                          : "Select warehouse"
                      }
                      selectOptions={warehouses.map((warehouse: any) => ({
                        id: warehouse.id,
                        value: warehouse.id,
                        text: warehouse.name,
                      }))}
                      validation={{ required: "Warehouse is required" }}
                      value={watch("warehouseId")}
                      onChange={handleWarehouseChange}
                      disabled={isWarehousesFetching}
                    />
                    {errors.warehouseId?.message && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.warehouseId.message}
                      </p>
                    )}
                  </div>

                  <InputField
                    className="col-span-3"
                    label={t("Email")}
                    placeholder="outlet@email.com"
                    type="email"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^\S+@\S+$/,
                        message: "Invalid email address",
                      },
                    })}
                    error={errors.email?.message}
                  />

                  <InputField
                    className="col-span-3"
                    label={t("Mobile")}
                    placeholder="96687651235"
                    type="text"
                    maxLength={10}
                    inputMode="numeric"
                    {...register("mobileNo", {
                      required: "Mobile is required",
                      pattern: {
                        value: /^\d{9,10}$/,
                        message: "Mobile number must be 9 or 10 digits",
                      },
                      validate: (value: string) => {
                        const trimmed = value?.trim();
                        if (/^0+$/.test(trimmed))
                          return "Mobile number cannot be all zeros";
                        return true;
                      },
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);
                        setValue("mobileNo", value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      },
                    })}
                    error={errors.mobileNo?.message}
                  />
                </div>
              </div>

              {/* ═══ Address Section (same design as Order Create ReceiverAddress) ═══ */}
              <div className="bg-[#fdfdf6] rounded-xl space-y-4 p-4 border w-full">
                <div className="space-y-4">
                  {/* Map Toggle */}
                  <div className="flex justify-between items-center w-full">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      Pin Location on Map
                      {isPinned && (
                        <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold">
                          📍 Pinned
                        </span>
                      )}
                    </label>
                    <button
                      type="button"
                      onClick={handleToggleMap}
                      className="px-3 py-1.5 rounded-md bg-[#4F6EF7] text-white text-xs font-semibold hover:bg-[#3d59c9] transition-colors"
                    >
                      {showMap ? "Hide Map" : "Open Map"}
                    </button>
                  </div>

                  {/* Google Map */}
                  {showMap && (
                    <OutletGoogleMap
                      onLocationPicked={handleMapPick}
                      pinPosition={pinPosition}
                      snappedPosition={snappedPosition}
                    />
                  )}

                  {/* ── Short Address + Full Address (Saudi Arabia) ── */}
                  {isSaudiArabia ? (
                    <div className="flex flex-row gap-4 w-full">
                      <div className="w-[30%] relative">
                        <label className="text-[#1E293B] text-xs">
                          {t("Short Address")} / {t("National Address")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            {...(() => {
                              const { onChange: formOnChange, ...rest } =
                                register("shortAddress", {
                                  required: "Short address is required",
                                  pattern: {
                                    value: /^[A-Z0-9]{8}$/,
                                    message:
                                      "Enter a valid 8-character short address",
                                  },
                                });
                              return {
                                ...rest,
                                onChange: (e: any) => {
                                  formOnChange(e);
                                  handleShortAddressChange(e);
                                },
                              };
                            })()}
                            placeholder="e.g. RHOO2375"
                            autoComplete="off"
                            disabled={shortAddressLoading}
                            className={`w-full rounded-lg py-2 px-3 border outline-none pr-9 transition-all
                                ${
                                  shortAddressLoading
                                    ? "border-blue-300 bg-blue-50"
                                    : shortAddressError
                                      ? "border-red-400 bg-red-50"
                                      : shortAddressWarning
                                        ? "border-yellow-500 bg-yellow-50"
                                        : "border-gray-300 bg-white"
                                } focus:border-[#FBD037]`}
                          />
                          <div className="absolute inset-y-0 right-2 flex items-center">
                            {shortAddressLoading && <Spinner />}
                            {!shortAddressLoading && shortAddressError && (
                              <span className="text-red-500 text-base">✕</span>
                            )}
                            {shortAddressOk && (
                              <span className="text-green-500 text-base">
                                ✓
                              </span>
                            )}
                          </div>
                        </div>
                        {shortAddressLoading && (
                          <div className="mt-1 flex items-center gap-1.5">
                            <div
                              className="h-2 w-2 rounded-full bg-blue-400 animate-bounce"
                              style={{ animationDelay: "0ms" }}
                            />
                            <div
                              className="h-2 w-2 rounded-full bg-blue-400 animate-bounce"
                              style={{ animationDelay: "150ms" }}
                            />
                            <div
                              className="h-2 w-2 rounded-full bg-blue-400 animate-bounce"
                              style={{ animationDelay: "300ms" }}
                            />
                            <span className="text-xs text-blue-500 font-medium">
                              Looking up address…
                            </span>
                          </div>
                        )}
                        {shortAddressError && !shortAddressLoading && (
                          <p className="mt-1 text-xs text-red-500">
                            ⚠ {shortAddressError}
                          </p>
                        )}
                        {errors.shortAddress?.message &&
                          !shortAddressError &&
                          !shortAddressLoading && (
                            <p className="mt-1 text-xs text-red-500">
                              {errors.shortAddress.message}
                            </p>
                          )}
                        {shortAddressWarning &&
                          !shortAddressError &&
                          !shortAddressLoading && (
                            <span className="text-xs text-yellow-600 mt-1 block font-medium">
                              {shortAddressWarning}
                            </span>
                          )}
                      </div>
                      <div className="w-[70%]">
                        <label className="text-[#1E293B] text-xs">
                          Full Address Line{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register("address", {
                            required: "Full address is required",
                          })}
                          placeholder="Enter full address details"
                          className={`w-full rounded-lg py-2 px-3 border outline-none ${
                            errors.address
                              ? "border-red-500"
                              : "border-gray-300"
                          } focus:border-[#FBD037]`}
                        />
                        {errors.address?.message && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.address.message}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* ── Full Address only (non-SA countries) ── */
                    <div className="w-full">
                      <label className="text-[#1E293B] text-xs">
                        Full Address Line{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...register("address", {
                          required: "Full address is required",
                        })}
                        placeholder="Enter full address details"
                        className={`w-full rounded-lg py-2 px-3 border outline-none ${
                          errors.address ? "border-red-500" : "border-gray-300"
                        } focus:border-[#FBD037]`}
                      />
                      {errors.address?.message && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.address.message}
                        </p>
                      )}
                    </div>
                  )}

                  {/* ── Latitude & Longitude ── */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-1">
                      <label className="text-[#1E293B] text-xs">
                        Latitude <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...register("latitude", {
                          required: "Latitude is required",
                          validate: (value: string) => {
                            if (!value) return "Latitude is required";
                            const num = parseFloat(value);
                            if (isNaN(num))
                              return "Latitude must be a valid number";
                            if (num < -90 || num > 90)
                              return "Latitude must be between -90 and 90";
                            return true;
                          },
                        })}
                        placeholder="e.g. 24.7136"
                        className={`w-full rounded-lg py-2 px-3 border outline-none ${
                          errors.latitude ? "border-red-500" : "border-gray-300"
                        } focus:border-[#FBD037]`}
                      />
                      {errors.latitude?.message && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.latitude.message}
                        </p>
                      )}
                    </div>
                    <div className="col-span-1">
                      <label className="text-[#1E293B] text-xs">
                        Longitude <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...register("longitude", {
                          required: "Longitude is required",
                          validate: (value: string) => {
                            if (!value) return "Longitude is required";
                            const num = parseFloat(value);
                            if (isNaN(num))
                              return "Longitude must be a valid number";
                            if (num < -180 || num > 180)
                              return "Longitude must be between -180 and 180";
                            return true;
                          },
                        })}
                        placeholder="e.g. 46.6753"
                        className={`w-full rounded-lg py-2 px-3 border outline-none ${
                          errors.longitude
                            ? "border-red-500"
                            : "border-gray-300"
                        } focus:border-[#FBD037]`}
                      />
                      {errors.longitude?.message && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.longitude.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ── City & District ── */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-1">
                      <SelectInput
                        name="city"
                        label={addressConfig.cityLabel}
                        placeholder={addressConfig.cityPlaceholder}
                        selectOptions={cities.map((city: any) => ({
                          id: city.id,
                          value: city.id,
                          text: city.name,
                        }))}
                        validation={{
                          required: `${addressConfig.cityLabel} is required`,
                        }}
                        value={selectedLocation?.city?.id}
                        onChange={handleCityChange}
                        disabled
                      />
                    </div>
                    <div className="col-span-1">
                      <SelectInput
                        name="district"
                        label={addressConfig.districtLabel}
                        placeholder={addressConfig.districtPlaceholder}
                        selectOptions={districts.map((district: any) => ({
                          id: district.id,
                          value: district.id,
                          text: district.name,
                        }))}
                        validation={{
                          required: `${addressConfig.districtLabel} is required`,
                        }}
                        value={selectedLocation?.district?.id}
                        onChange={handleDistrictChange}
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>{" "}
          <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end space-x-2  bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-[30px] ">
            <div className="col-span-6 flex justify-end gap-2">
              <button
                type="submit"
                disabled={isPending}
                className="px-6 py-2 rounded-full bg-[#FBD037]"
              >
                {isEditMode
                  ? isPending
                    ? "Updating..."
                    : "Update Outlet"
                  : isPending
                    ? "Creating..."
                    : "Create Outlet"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AddNewOutlet;
